import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./Dec2025_Womens_T20EventReport.css"; // same CSS works
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { FiDownload } from "react-icons/fi";

export default function Dec2025WomensT20EventReport() {
    const API = "https://node.aattumtpl.com/api/admin/eventreports/dec2025-womens-t20-report";
    const SUMMARY_API = "https://node.aattumtpl.com/api/admin/eventreports/dec2025-womens-t20-summary";

    const [data, setData] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [teamNames, setTeamNames] = useState([]);
    const [cities, setCities] = useState([]);

    const [teamCount, setTeamCount] = useState(0);
    const [individualCount, setIndividualCount] = useState(0);
    const [registrationCount, setRegistrationCount] = useState(0);
    const [registrationUserIds, setRegistrationUserIds] = useState([]);

    const [type, setType] = useState("All");
    const [teamName, setTeamName] = useState("");
    const [city, setCity] = useState("");
    const [userIdFilter, setUserIdFilter] = useState("");
    const [search, setSearch] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [expandedPlayers, setExpandedPlayers] = useState({});
    const [expandedSubs, setExpandedSubs] = useState({});

    const togglePlayers = (id) => {
        setExpandedPlayers((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const toggleSubs = (id) => {
        setExpandedSubs((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    useEffect(() => {
        fetchSummary();
        fetchData();
    }, []);

    const fetchSummary = async () => {
        const res = await axios.get(SUMMARY_API);
        setTeamCount(res.data.summary.teams);
        setIndividualCount(res.data.summary.individuals);
        setRegistrationCount(res.data.summary.registrations);
        setRegistrationUserIds(res.data.summary.registrationUserIds || []);
    };

    const fetchData = async () => {
        const res = await axios.get(API);
        setData(res.data.data);
        setFiltered(res.data.data);
        setTeamNames(res.data.filters.teamNames);
        setCities(res.data.filters.cities);
    };

    useEffect(() => {
        let temp = [...data];

        if (type !== "All") temp = temp.filter((item) => item.type === type);
        if (teamName) temp = temp.filter(
            (item) => item.teamName === teamName
        );

        if (city) temp = temp.filter((item) => item.city === city);
        if (userIdFilter.trim())
            temp = temp.filter((item) =>
                item.userId?.toLowerCase().includes(userIdFilter.toLowerCase())
            );
        if (search.trim())
            temp = temp.filter((item) =>
                JSON.stringify(item).toLowerCase().includes(search.toLowerCase())
            );

        if (startDate)
            temp = temp.filter(
                (item) => new Date(item.createdAt) >= new Date(startDate)
            );
        if (endDate)
            temp = temp.filter(
                (item) => new Date(item.createdAt) <= new Date(endDate)
            );

        setFiltered(temp);
    }, [type, teamName, city, userIdFilter, search, startDate, endDate]);



    const exportExcel = () => {
        // Format each row in filtered
        const formattedData = filtered.map(row => {
            const formatPlayers = (list) => {
                if (!list || !Array.isArray(list)) return "";
                return list
                    .map(p => `${p.no}. ${p.name} (${p.age} yrs • ${p.position} • ${p.arm})`)
                    .join("\r\n"); // just line break, no comma
            };

            return {
                ...row,
                players: formatPlayers(row.players),
                substitutes: formatPlayers(row.substitutes),
            };
        });

        // Convert to worksheet
        const ws = XLSX.utils.json_to_sheet(formattedData);

        // Adjust column widths for readability
        ws['!cols'] = Object.keys(formattedData[0]).map(key => ({ wch: 30 }));

        // Create workbook and append worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        // Export file
        XLSX.writeFile(wb, "Dec2025_Womens_T20_Report.xlsx");
    };


    const exportCSV = () => {
        // Format each row
        const formattedData = filtered.map(row => {
            const formatPlayers = (list) => {
                if (!list || !Array.isArray(list)) return "";
                return list
                    .map(p => `${p.no}. ${p.name} (${p.age} yrs • ${p.position} • ${p.arm})`)
                    .join("\n"); // newline within cell
            };

            return {
                ...row,
                // wrap multi-line fields in quotes for CSV
                players: `"${formatPlayers(row.players)}"`,
                substitutes: `"${formatPlayers(row.substitutes)}"`,
            };
        });

        // Convert to worksheet and then CSV
        const ws = XLSX.utils.json_to_sheet(formattedData);
        const csv = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

        saveAs(blob, "Dec2025_Womens_T20_Report.csv");
    };



    const exportPDF = () => {
        const doc = new jsPDF({
            orientation: 'l',
            unit: 'mm',
            format: [1200, 300], // width x height in mm
        });

        doc.text("Dec 2025 Women's T20 Event Report", 14, 10);

        // Format data
        const formattedData = filtered.map(row => {
            const formatPlayers = (list) => {
                if (!list || !Array.isArray(list) || list.length === 0) return "";
                return list
                    .map(p => `${p.no}. ${p.name} (${p.age} yrs • ${p.position} • ${p.arm})`)
                    .join("\n");
            };

            return {
                ...row,
                players: row.players ? formatPlayers(row.players) : "",
                substitutes: row.substitutes ? formatPlayers(row.substitutes) : "",
            };
        });

        // Get all column keys (field names)
        const columns = Object.keys(formattedData[0] || {}).map(key => ({ header: key, dataKey: key }));

        autoTable(doc, {
            startY: 20,
            columns: columns,            // use columns instead of head
            body: formattedData,
            styles: {
                fontSize: 7,
                cellWidth: 'wrap',
                overflow: 'linebreak',
                minCellHeight: 4,
            },
            columnStyles: {
                players: { cellWidth: 150 },
                substitutes: { cellWidth: 150 },
                name: { cellWidth: 40 },
                team: { cellWidth: 50 },
                position: { cellWidth: 40 },
                age: { cellWidth: 20 },
            },
            headStyles: { fillColor: [41, 128, 185], halign: 'center' },
            bodyStyles: { valign: 'top' },
            theme: 'grid',
        });

        doc.save("Dec2025_Womens_T20_Report.pdf");
    };


    const exportSingleRow = (row, idx) => {
        const rowId = row.userId || idx;

        const formattedRow = {
            ...row,
            players: row.players && Array.isArray(row.players)
                ? row.players
                    .map(p => `${p.no}. ${p.name} (${p.age} yrs • ${p.position} • ${p.arm})`)
                    .join(",\r\n") // comma + line break
                : "",
            substitutes: row.substitutes && Array.isArray(row.substitutes)
                ? row.substitutes
                    .map(p => `${p.no}. ${p.name} (${p.age} yrs • ${p.position} • ${p.arm})`)
                    .join(",\r\n") // comma + line break
                : "",
        };

        const ws = XLSX.utils.json_to_sheet([formattedRow]);

        // Adjust column width for readability
        ws['!cols'] = Object.keys(formattedRow).map(key => ({ wch: 30 }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Row Data");

        XLSX.writeFile(wb, `Row-${rowId}.xlsx`);
    };

    return (
        <>
            <div className="KK-admin-container11">
                <h1 className="KK-admin-title11">Women's T20 Event Report - DEC 2025</h1>

                <div className="KK-filter-box11">
                    <select className="KK-select11" onChange={(e) => setType(e.target.value)} value={type}>
                        <option value="All">All</option>
                        <option value="Team">Team</option>
                        <option value="Individual">Individual</option>
                    </select>

                    <select
                        className="KK-select11"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                    >
                        <option value="">Team Name</option>
                        {teamNames.map((name) => (
                            <option key={name} value={name}>
                                {name}
                            </option>
                        ))}
                    </select>

                    <select className="KK-select11" value={city} onChange={(e) => setCity(e.target.value)}>
                        <option value="">City</option>
                        {cities.map((c) => (
                            <option key={c}>{c}</option>
                        ))}
                    </select>

                    <input
                        type="text"
                        placeholder="Search by User ID..."
                        className="KK-input11"
                        value={userIdFilter}
                        onChange={(e) => setUserIdFilter(e.target.value)}
                    />

                    <input
                        type="text"
                        placeholder="Search anything..."
                        className="KK-input11"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <input
                        type="date"
                        className="KK-input11"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                    <input
                        type="date"
                        className="KK-input11"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />

                    {/* Clear Filters Button */}
                    <button
                        className="KK-btn111"
                        onClick={() => {
                            setType("All");
                            setTeamName("");
                            setCity("");
                            setUserIdFilter("");
                            setSearch("");
                            setStartDate("");
                            setEndDate("");
                        }}
                    >
                        Clear Filters
                    </button>
                </div>


                <div className="KK-download-buttons11">
                    <button className="KK-btn11" onClick={exportExcel}>Excel</button>
                    <button className="KK-btn11" onClick={exportCSV}>CSV</button>
                    <button className="KK-btn11" onClick={exportPDF}>PDF</button>
                </div>

                <div className="KK-table-wrapper11">
                    <div className="KK-table-scroll11">
                        <table className="KK-styled-table11">
                            <thead>
                                <tr>
                                    {filtered.length > 0 &&
                                        Object.keys(filtered[0]).map((key) => (
                                            <th key={key}>{key.toUpperCase()}</th>
                                        ))}
                                    <th>ACTION</th>   {/* NEW COLUMN */}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length > 0 ? (
                                    filtered.map((row, idx) => {
                                        const rowId = row.userId || idx;
                                        return (
                                            <React.Fragment key={rowId}>

                                                <tr>
                                                    {Object.entries(row).map(([key, val], k) => (
                                                        <td
                                                            key={k}
                                                            className={
                                                                key === "players" || key === "substitutes"
                                                                    ? "td-left11"
                                                                    : "td-center11"
                                                            }
                                                        >
                                                            {key === "players" && Array.isArray(val) ? (
                                                                <>
                                                                    {val.length} Players
                                                                    {val.length > 0 && (
                                                                        <button
                                                                            className="eye-btn11"
                                                                            onClick={() => togglePlayers(rowId)}
                                                                        >
                                                                            {expandedPlayers[rowId] ? <FiChevronUp /> : <FiChevronDown />}

                                                                        </button>
                                                                    )}
                                                                </>
                                                            ) : key === "substitutes" && Array.isArray(val) ? (
                                                                <>
                                                                    {val.length} Substitutes
                                                                    {val.length > 0 && (
                                                                        <button
                                                                            className="eye-btn11"
                                                                            onClick={() => toggleSubs(rowId)}
                                                                        >
                                                                            {expandedSubs[rowId] ? <FiChevronUp /> : <FiChevronDown />}

                                                                        </button>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                val
                                                            )}
                                                        </td>

                                                    ))}
                                                    <td className="td-center11">
                                                        <button
                                                            className="KK-small-download11"
                                                            onClick={() => exportSingleRow(row)}
                                                            title="Download Excel"
                                                        >
                                                            <FiDownload size={16} />
                                                        </button>
                                                    </td>

                                                </tr>


                                                {/* Expanded row for players */}
                                                {expandedPlayers[rowId] && (
                                                    <tr className="expanded-row11">
                                                        <td colSpan={Object.keys(row).length}>
                                                            <div className="expanded-content11">
                                                                <strong>Players</strong>
                                                                {row.players.map((p) => (
                                                                    <div key={p._id || p.no} className="player-row11">
                                                                        <span className="player-no11">{p.no}.</span>
                                                                        <span className="player-name11">{p.name}</span>
                                                                        <span className="player-details11">
                                                                            ({p.age} yrs • {p.position} • {p.arm})
                                                                        </span>
                                                                    </div>


                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                                {/* Expanded row for substitutes */}
                                                {expandedSubs[rowId] && (
                                                    <tr className="expanded-row11">
                                                        <td colSpan={Object.keys(row).length}>
                                                            <div className="expanded-content11">
                                                                <strong>Substitutes</strong>
                                                                {row.substitutes.map((s) => (
                                                                    <div key={s._id || s.no} className="sub-row11">
                                                                        <span className="sub-no11">{s.no}.</span>
                                                                        <span className="sub-name11">{s.name}</span>
                                                                        <span className="sub-details11">
                                                                            ({s.age} yrs • {s.position} • {s.arm})
                                                                        </span>
                                                                    </div>


                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={Object.keys(filtered[0] || {}).length + 1}
                                            style={{ textAlign: "center", padding: "10px" }}
                                        >
                                            No records found
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                        </table>
                    </div>
                </div>

            </div>
        </>
    );
}
